import { useCallback, useEffect, useState } from "react";
import type { MeResponse } from "@juegossc/shared";
import { adminMe } from "../lib/api";
import {
  getDevAdminEmail,
  setDevAdminEmail,
  clearDevAdminEmail,
} from "../lib/admin-storage";

export type AdminUser = MeResponse;

// Carga info del user logueado (CF Access en prod, email override en dev).
// Si me() falla con 401, indica que no hay rol asignado.
export function useAdminUser() {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [devEmail, setDevEmailState] = useState<string | null>(() =>
    getDevAdminEmail(),
  );

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await adminMe();
      setUser(me);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch, devEmail]);

  const setDevEmail = useCallback((email: string) => {
    setDevAdminEmail(email);
    setDevEmailState(email);
  }, []);

  const clearDev = useCallback(() => {
    clearDevAdminEmail();
    setDevEmailState(null);
  }, []);

  return {
    user,
    loading,
    error,
    devEmail,
    setDevEmail,
    clearDev,
    refetch,
    isDev: import.meta.env.DEV,
  };
}

// "Sucursal efectiva" — qué sucursal usar para queries admin:
// - Si user es admin: su propia sucursal (no requiere as_sucursal).
// - Si user es super_admin: debe elegir una manualmente (UI maneja eso).
export function effectiveSucursal(
  user: AdminUser | null,
  selected?: number,
): number | undefined {
  if (!user) return undefined;
  if (user.role === "super_admin") return selected;
  if (user.role === "admin") return user.sucursal_id ?? undefined;
  return undefined;
}
